import { z } from 'zod'
import { guidSchema } from '../../contracts'
import { bzOptional, EquipmentType } from '..'
import { ONLINE_BOOKING_SERVICE_TYPES } from '../OnlineBooking/OnlineBooking'
import { JobLeadContactInformationSchema } from './_JobLeadContactInfo'

export const JOB_LEAD_STATUSES = ['open', 'converted', 'archived'] as const

export type JobLeadStatus = (typeof JOB_LEAD_STATUSES)[number]

export const MAINTENANCE_EQUIPMENT_TYPES = [
  EquipmentType.AIR_CONDITIONER,
  EquipmentType.BOILER,
  EquipmentType.GAS_FURNACE,
  EquipmentType.GENERATOR,
  EquipmentType.HEAT_PUMP,
  EquipmentType.HUMIDIFIER,
  EquipmentType.DEHUMIDIFIER,
  EquipmentType.WATER_HEATER,
  EquipmentType.FULL_SYSTEM,
  EquipmentType.FURNACE_ONLY,
  EquipmentType.AC_ONLY,
  EquipmentType.CONDENSER_ONLY,
  EquipmentType.EVAPORATOR_COIL_ONLY,
  EquipmentType.HEAT_PUMP_ONLY,
  EquipmentType.AIR_HANDLER_ONLY,
  EquipmentType.DUCTLESS_MINI_SPLIT_SYSTEM,
  EquipmentType.DUCTLESS_CONDENSER,
  EquipmentType.DUCTLESS_INSIDE_HEAD_UNIT,
  EquipmentType.PACKAGE_UNIT,
  EquipmentType.HYDRONIC_SYSTEM,
  EquipmentType.WALL_MOUNTED_UNIT,
  EquipmentType.FLOOR_MOUNTED_UNIT,
  EquipmentType.CONCEALED_DUCTED_UNIT,
  EquipmentType.ONE_WAY_CEILING_CASSETTE,
  EquipmentType.FOUR_WAY_CEILING_CASSETTE,
  EquipmentType.WALL_MOUNT_DUCTLESS_FAN_COIL,
  EquipmentType.FLOOR_MOUNT_DUCTLESS_FAN_COIL,
  EquipmentType.INDOOR_COIL,
] as const satisfies EquipmentType[]

export const SERVICE_EQUIPMENT_TYPES = [
  ...MAINTENANCE_EQUIPMENT_TYPES,
  EquipmentType.AIR_PURIFIER,
  EquipmentType.THERMOSTAT,
] as const satisfies EquipmentType[]

export const JOB_LEAD_EQUIPMENT_TYPES = [...MAINTENANCE_EQUIPMENT_TYPES, ...SERVICE_EQUIPMENT_TYPES] as const
export type JobLeadEquipmentTypeArray = Array<(typeof JOB_LEAD_EQUIPMENT_TYPES)[number]>

export const JOB_LEAD_ESTIMATE_TYPES = ['COOLING_SYSTEM', 'HEATING_SYSTEM', 'FULL_SYSTEM'] as const
export type JobLeadEstimateType = (typeof JOB_LEAD_ESTIMATE_TYPES)[number]
export const JobLeadEstimateTypeDisplayNames: Record<JobLeadEstimateType, string> = {
  COOLING_SYSTEM: 'Cooling',
  HEATING_SYSTEM: 'Heating',
  FULL_SYSTEM: 'Full System',
}

export const JobLeadAvailabilitySchema = z.object({
  times: z.array(
    z.object({
      type: z.string(),
      value: z.string(),
    }),
  ),
})

export type JobLeadAvailability = z.infer<typeof JobLeadAvailabilitySchema>

const PhoneCallMetadata = z.object({
  type: z.literal('phone-call'),
  data: z.object({
    externalCallId: bzOptional(z.string()),
    summary: bzOptional(z.string()),
    transcript: bzOptional(z.string()),
    recordingUrl: bzOptional(z.string()),
  }),
})

export const JobLeadAdditionalMetadataSchema = z.discriminatedUnion('type', [PhoneCallMetadata])

export type JobLeadAdditionalMetadata = z.infer<typeof JobLeadAdditionalMetadataSchema>

export const CreateJobLeadRequestSchema = JobLeadContactInformationSchema.extend({
  jobLeadGuid: bzOptional(guidSchema),

  // Request Details
  requestType: bzOptional(z.enum(ONLINE_BOOKING_SERVICE_TYPES).describe('Type of Request')),
  appointmentAvailabilities: bzOptional(
    JobLeadAvailabilitySchema.describe('Appointment Availability').or(z.string().array()),
  ),
  jobSummary: bzOptional(z.string().describe('Additional Details')),
  leadSource: bzOptional(z.string().describe('Lead Source')),
  submittedJobTypeGuid: bzOptional(guidSchema),
  maintenancePlanVisitGuid: bzOptional(guidSchema),

  // Deprecated fields
  additionalMetadata: bzOptional(JobLeadAdditionalMetadataSchema),

  accountType: bzOptional(z.string().describe('Type of Customer')),
  /** @deprecated */
  jobType: bzOptional(z.string().describe('Type of Service')),
  /** @deprecated */
  equipmentTypes: bzOptional(
    z.array(z.enum([...MAINTENANCE_EQUIPMENT_TYPES, ...SERVICE_EQUIPMENT_TYPES])).describe('Equipment Types'),
  ),
  /** @deprecated */
  estimateType: bzOptional(z.enum(JOB_LEAD_ESTIMATE_TYPES).describe('Estimate Type')),
})

export type CreateJobLeadRequest = z.infer<typeof CreateJobLeadRequestSchema>

export const GetUnauthJobLeadRequestSchema = z.object({
  maintenancePlanVisitGuid: guidSchema,
})

export type GetUnauthJobLeadRequest = z.infer<typeof GetUnauthJobLeadRequestSchema>
